import React from 'react';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { Card, CardContent, CardHeader, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { formatDate, formatDateTime } from '../../../../../services/functions';
import { createEmploymeeInformationAction, deleteEmploymeeInformationAction } from '../../../../../services/request';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useEmployees } from '../../EmployessContext';

function RowForm({ formik, handleClose, loading }) {
  return (
    <TableRow>
      <TableCell width={150}>
        <Stack direction="row" justifyContent="space-around">
          {loading
            ? <CircularProgress size={30}/>
            : <>
              <Tooltip title="zapisz">
                <IconButton color="primary" onClick={formik.handleSubmit}>
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="anuluj">
                <IconButton color="error" onClick={handleClose}>
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </>}
        </Stack>
      </TableCell>
      <TableCell align="left">
        <SmallFormTextField
          name="text"
          onChange={formik.handleChange}
          value={formik.values.text}
          multiline={true}
          sx={{ width: '100%' }}
        />
      </TableCell>
    </TableRow>
  );
}


function Informations() {
  const { rowData, setRowData } = useEmployees();
  const [open, setOpen] = React.useState(false);
  const [editedId, setEditedId] = React.useState(null);
  const { loading, mutate } = useMutation(createEmploymeeInformationAction);
  const { mutate: deleteMutate } = useMutation(deleteEmploymeeInformationAction);

  const formik = useFormik({
    initialValues: {
      text: '',
      type: 1
    },
    validateOnChange: false,
    onSubmit: values => {
      if (editedId === null) {
        values.employee = rowData.id;
      }
      handleSubmit(values);
    }
  });

  const handleClose = () => {
    setOpen(false);
    setEditedId(null);
    formik.resetForm();
  };


  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, editedId);

    if (!error) {
      if (editedId === null) {
        setRowData(prev => ({ ...prev, informations: [payload, ...prev.informations] }));
      } else {
        setRowData(prev => ({ ...prev, informations: prev.informations.map(item => item.id === editedId ? payload : item) }));
      }
      handleClose();
    };
  };

  const handleDelete = async id => {
    const { error } = await deleteMutate(id);
    if (!error) {
      setRowData(prev => ({ ...prev, informations: prev.informations.filter(item => item.id !== id) }));
    };
  };

  return (
    <Card sx={{ flex: 0.49 }}>
      <CardHeader
        title="Informacje"
        action={
          editedId !== null || open
            ? null
            : <Tooltip title="dodaj">
              <IconButton color="primary" onClick={() => {
                setOpen(true);
                setEditedId(null);
              }}>
                <AddCircleIcon />
              </IconButton>
            </Tooltip>

        }/>
      <CardContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                </TableCell>
                <TableCell align="left">Informacja</TableCell>
                <TableCell align="left">Data</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {open ? <RowForm formik={formik} handleClose={handleClose} loading={loading}/> : null}
              {rowData.informations?.map(item => item.type !== 1 ? null : (
                editedId === item.id
                  ? <RowForm formik={formik} handleClose={handleClose} loading={loading}/>
                  : <TableRow key={item.id}>
                    <TableCell align="center" width={150}>
                      <Stack direction="row" justifyContent="space-around">
                        <IconButton color="primary" onClick={() => {
                          setEditedId(item.id);
                          formik.setValues({ text: item.text });
                          setOpen(false);
                        }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(item.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell align="left" style={{ wordBreak: 'break-word' }}>{item.text}</TableCell>
                    <TableCell align="left" width={120}>{formatDate(item.created_at)}</TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

function Comments() {
  const { rowData, setRowData } = useEmployees();
  const [open, setOpen] = React.useState(false);
  const [editedId, setEditedId] = React.useState(null);
  const { loading, mutate } = useMutation(createEmploymeeInformationAction);
  const { mutate: deleteMutate } = useMutation(deleteEmploymeeInformationAction);

  const formik = useFormik({
    initialValues: {
      text: '',
      type: 2
    },
    validateOnChange: false,
    onSubmit: values => {
      if (editedId === null) {
        values.employee = rowData.id;
      }
      handleSubmit(values);
    }
  });

  const handleClose = () => {
    setOpen(false);
    setEditedId(null);
    formik.resetForm();
  };


  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, editedId);

    if (!error) {
      if (editedId === null) {
        setRowData(prev => ({ ...prev, informations: [payload, ...prev.informations] }));
      } else {
        setRowData(prev => ({ ...prev, informations: prev.informations.map(item => item.id === editedId ? payload : item) }));
      }
      handleClose();
    };
  };

  const handleDelete = async id => {
    const { error } = await deleteMutate(id);
    if (!error) {
      setRowData(prev => ({ ...prev, informations: prev.informations.filter(item => item.id !== id) }));
    };
  };

  return (
    <Card sx={{ flex: 0.49 }}>
      <CardHeader
        title="Ustalenia"
        action={
          editedId !== null || open
            ? null
            : <Tooltip title="dodaj">
              <IconButton color="primary" onClick={() => {
                setOpen(true);
                setEditedId(null);
              }}>
                <AddCircleIcon />
              </IconButton>
            </Tooltip>

        }/>
      <CardContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                </TableCell>
                <TableCell align="left">Informacja</TableCell>
                <TableCell align="left">Data</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {open ? <RowForm formik={formik} handleClose={handleClose} loading={loading}/> : null}
              {rowData.informations?.map(item => item.type !== 2 ? null : (
                editedId === item.id
                  ? <RowForm formik={formik} handleClose={handleClose} loading={loading}/>
                  : <TableRow key={item.id}>
                    <TableCell align="center" width={150}>
                      <Stack direction="row" justifyContent="space-around">
                        <IconButton color="primary" onClick={() => {
                          setEditedId(item.id);
                          formik.setValues({ text: item.text });
                          setOpen(false);
                        }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(item.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell align="left" style={{ wordBreak: 'break-word' }}>{item.text}</TableCell>
                    <TableCell align="left" width={120}>{formatDate(item.created_at)}</TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default function InformationsComments() {

  return (
    <Stack flexDirection="row" justifyContent="space-between">
      <Informations />
      <Comments />
    </Stack>
  );
};
